const utf8 = new TextEncoder();
const utf8Decoder = new TextDecoder("utf-8", { fatal: false });

const SIG_LOCAL_FILE = 0x04034b50;
const SIG_CENTRAL_FILE = 0x02014b50;
const SIG_EOCD = 0x06054b50;

export const MAX_ZIP_BYTES = 25 * 1024 * 1024;

export class ZipSizeError extends Error {}

function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) {
    crc ^= data[i]!;
    for (let j = 0; j < 8; j++) crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

/** Creates an uncompressed ZIP as a Blob from a map of paths to string contents. */
export function createZip(
  files: Record<string, string>,
  opts: { maxBytes?: number } = {}
): Blob {
  const entries = Object.entries(files)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([name, content]) => {
      const nameBytes = utf8.encode(name);
      const data = utf8.encode(content);
      return { name: nameBytes, data, crc: crc32(data), offset: 0 };
    });

  let localSize = 0;
  for (const e of entries) {
    e.offset = localSize;
    localSize += 30 + e.name.length + e.data.length;
  }

  let centralSize = 0;
  for (const e of entries) centralSize += 46 + e.name.length;

  const size = localSize + centralSize + 22;
  if (opts.maxBytes !== undefined && size > opts.maxBytes) {
    throw new ZipSizeError(`ZIP exceeds ${opts.maxBytes} bytes`);
  }
  const buf = new ArrayBuffer(size);
  const out = new Uint8Array(buf);
  const view = new DataView(buf);
  let pos = 0;

  for (const e of entries) {
    view.setUint32(pos, SIG_LOCAL_FILE, true);
    view.setUint16(pos + 4, 20, true);
    view.setUint32(pos + 14, e.crc, true);
    view.setUint32(pos + 18, e.data.length, true);
    view.setUint32(pos + 22, e.data.length, true);
    view.setUint16(pos + 26, e.name.length, true);
    pos += 30;
    out.set(e.name, pos);
    pos += e.name.length;
    out.set(e.data, pos);
    pos += e.data.length;
  }

  const centralStart = pos;
  for (const e of entries) {
    view.setUint32(pos, SIG_CENTRAL_FILE, true);
    view.setUint16(pos + 4, 20, true);
    view.setUint16(pos + 6, 20, true);
    view.setUint32(pos + 16, e.crc, true);
    view.setUint32(pos + 20, e.data.length, true);
    view.setUint32(pos + 24, e.data.length, true);
    view.setUint16(pos + 28, e.name.length, true);
    view.setUint32(pos + 42, e.offset, true);
    pos += 46;
    out.set(e.name, pos);
    pos += e.name.length;
  }

  view.setUint32(pos, SIG_EOCD, true);
  view.setUint16(pos + 8, entries.length, true);
  view.setUint16(pos + 10, entries.length, true);
  view.setUint32(pos + 12, pos - centralStart, true);
  view.setUint32(pos + 16, centralStart, true);

  return new Blob([buf], { type: "application/zip" });
}

// Parses an uncompressed or DEFLATE-compressed ZIP into a `{ path: content }` map.
export async function parseZip(
  blob: Blob,
  opts: { maxTotalBytes?: number } = {}
): Promise<Record<string, string>> {
  const maxTotal = opts.maxTotalBytes ?? MAX_ZIP_BYTES;
  if (blob.size > maxTotal) {
    throw new ZipSizeError(`ZIP exceeds ${maxTotal} bytes`);
  }

  const bytes = new Uint8Array(await blob.arrayBuffer());
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  let eocd = -1;
  const minEocd = Math.max(0, bytes.length - 22 - 0xffff);
  for (let i = bytes.length - 22; i >= minEocd; i--) {
    if (view.getUint32(i, true) === SIG_EOCD) {
      eocd = i;
      break;
    }
  }
  if (eocd === -1) throw new Error("Not a valid ZIP archive");

  const numEntries = view.getUint16(eocd + 10, true);
  const cdOffset = view.getUint32(eocd + 16, true);

  const result: Record<string, string> = {};
  let totalBytes = 0;
  let pos = cdOffset;
  for (let i = 0; i < numEntries; i++) {
    if (view.getUint32(pos, true) !== SIG_CENTRAL_FILE) {
      throw new Error("Malformed ZIP: bad central directory entry");
    }
    const method = view.getUint16(pos + 10, true);
    const compressedSize = view.getUint32(pos + 20, true);
    const nameLen = view.getUint16(pos + 28, true);
    const extraLen = view.getUint16(pos + 30, true);
    const commentLen = view.getUint16(pos + 32, true);
    const localOffset = view.getUint32(pos + 42, true);
    const name = utf8Decoder.decode(
      bytes.subarray(pos + 46, pos + 46 + nameLen)
    );
    pos += 46 + nameLen + extraLen + commentLen;

    if (name.endsWith("/")) continue;

    if (
      name.startsWith("/") ||
      name.includes("\\") ||
      name.split("/").includes("..")
    ) {
      throw new Error(`Unsafe path in ZIP: ${name}`);
    }

    if (view.getUint32(localOffset, true) !== SIG_LOCAL_FILE) {
      throw new Error("Malformed ZIP: bad local file header");
    }
    const lhNameLen = view.getUint16(localOffset + 26, true);
    const lhExtraLen = view.getUint16(localOffset + 28, true);
    const dataStart = localOffset + 30 + lhNameLen + lhExtraLen;
    const data = bytes.subarray(dataStart, dataStart + compressedSize);

    // The declared uncompressed size is attacker-controlled, so the cap is
    // enforced on the actual bytes a deflate entry expands to.
    let content: string;
    if (method === 0) {
      totalBytes += data.length;
      if (totalBytes > maxTotal) {
        throw new ZipSizeError(
          `ZIP uncompressed size exceeds ${maxTotal} bytes`
        );
      }
      content = utf8Decoder.decode(data);
    } else if (method === 8) {
      const reader = new Blob([data])
        .stream()
        .pipeThrough(new DecompressionStream("deflate-raw"))
        .getReader();
      const chunks: Uint8Array[] = [];
      let entryBytes = 0;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        totalBytes += value.length;
        if (totalBytes > maxTotal) {
          await reader.cancel();
          throw new ZipSizeError(
            `ZIP uncompressed size exceeds ${maxTotal} bytes`
          );
        }
        chunks.push(value);
        entryBytes += value.length;
      }
      const merged = new Uint8Array(entryBytes);
      let offset = 0;
      for (const chunk of chunks) {
        merged.set(chunk, offset);
        offset += chunk.length;
      }
      content = utf8Decoder.decode(merged);
    } else {
      throw new Error(
        `Unsupported ZIP compression method ${method} for "${name}"`
      );
    }

    result[name] = content;
  }
  return result;
}
